import { useState, useRef, useReducer, useEffect } from "react"
import { Tabs, notification, Tour } from "antd"
import { MapRef } from 'react-map-gl/mapbox'
import { DragEndEvent } from '@dnd-kit/core'
import 'mapbox-gl/dist/mapbox-gl.css'
import AddJobs from "../RouteAdd/AddJobs"
import AddJobsByMapPreRender from "../RouteAdd/AddJobsByMap/AddJobsByMapPreRender"
import { useCrewQuery, useRouteByIdQuery, useRouteJobsQuery, useUpdateRouteMutation, useRegenerateRouteMutation, useJobsForCreateRouteQuery, useInsertUpdateRouteJobsMutation, useSaveRouteActiveMutation } from "../routingQueries"
import { RouteJob } from "../routingApi"
import { defaultRouteViewState, reducer, RouteLocation, dragRouteOrderChanged, createMapboxCoordQuery, getViewStateFromLocation, prepareRouteJobsForSave, getStartingLocation, getEndingLocation, buildJobLocations } from "./routeViewModel"
import { useTenantLocationsDataQuery } from '../../../domain/tenantLocations/tenantLocationsQueries'
import { TenantLocationType } from '../../../domain/tenantLocations/tenantLocation'
import { DEFAULT_MAP_ZOOM } from '../RouteAdd/routeAddModel'
import { Feature, LineString } from 'geojson'
import mapboxgl from 'mapbox-gl'
import ManageShopAddressModal from "../RouteAdd/ManageShopAddressModal"
import UpdateRouteModal from "./UpdateRouteModal"
import RegenerateRoutesModal from "./RegenerateRoutesModal"
import RouteViewSettings from "./RouteViewSettings"
import RouteViewMap from "./RouteViewMap"
import RouteViewJobList from "./RouteViewJobList"
import RouteViewTitleRow from "./RouteViewTitleRow"
import SaveRouteModal from "./SaveRouteModal"
import OptimizeRouteSection from "./OptimizeRouteSection"
import { getDrivingDirections } from "../Mappers/mapbox"
import { googleMapsOptimizeRoute } from "../Mappers/googleMaps"

const tabItems = [
  { label: 'Route Planner', key: '1' },
  { label: 'Settings', key: '2' }
]

export type Props = {
  routeId: number
  onBackToList: () => void
  newRoute: boolean
}

const RouteView = ({ routeId, onBackToList, newRoute }: Props) => {  
  const [state, dispatch] = useReducer(reducer, defaultRouteViewState)
  const [activeTab, setActiveTab] = useState('1')
  const [isAddJobsDrawerOpen, setIsAddJobsDrawerOpen] = useState(false)
  const [editJobId, setEditJobId] = useState<number | null>(null)
  const [isManageShopAddressModalOpen, setIsManageShopAddressModalOpen] = useState(false)
  const [isUpdateRouteModalOpen, setIsUpdateRouteModalOpen] = useState(false)
  const [isRegenerateRoutesModalOpen, setIsRegenerateRoutesModalOpen] = useState(false)
  const [isSaveRouteModalOpen, setIsSaveRouteModalOpen] = useState(false)
  const [pendingRouteUpdate, setPendingRouteUpdate] = useState<any>(null)
  const [hasInitiallyLoadedRouteJobs, setHasInitiallyLoadedRouteJobs] = useState(false)
  const [shouldPreRenderAddJobsMap, setShouldPreRenderAddJobsMap] = useState(false)
  const routeNameHeaderRef = useRef<HTMLDivElement>(null)
  const hasScrolled = useRef(false)

  const mapRef = useRef<MapRef>(null)
  const [hoveredJobSortIndex, setHoveredJobSortIndex] = useState<number | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapStyle, setMapStyle] = useState<string>("mapbox://styles/mapbox/light-v11")
  const [isStyleChanging, setIsStyleChanging] = useState(false)
  const [currentRouteData, setCurrentRouteData] = useState<any>(null)
  
  const [isTourOpen, setIsTourOpen] = useState(false)

  const { data: route } = useRouteByIdQuery(routeId)
  const { data: crew } = useCrewQuery()
  const { data: routeJobs, refetch: refetchRouteJobs } = useRouteJobsQuery(routeId)
  const { data: jobsForCreateRoute } = useJobsForCreateRouteQuery()

  const { data: tenantLocations } = useTenantLocationsDataQuery()
  const shopLocation = tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)
  const [locations, setLocations] = useState<RouteLocation[]>([])

  const updateRouteMutation = useUpdateRouteMutation()
  const regenerateRoute = useRegenerateRouteMutation()
  const insertUpdateRouteJobsMutation = useInsertUpdateRouteJobsMutation()
  const saveRouteActiveMutation = useSaveRouteActiveMutation()

  const [totalTime, setTotalTime] = useState(0)
  const [distance, setDistance] = useState(0)
  const [optimizeRange, setOptimizeRange] = useState<[number, number]>([1, 23])

  const defaultViewState = {
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: DEFAULT_MAP_ZOOM
  }

  const [viewState, setViewState] = useState(getViewStateFromLocation(state.startingLocation, defaultViewState))

  const displayDrivingDirections = async () => {
    const coords = createMapboxCoordQuery(getStartingLocation(state.jobs, shopLocation, jobsForCreateRoute), state.endingLocation, jobsForCreateRoute, state.jobs)

    if(coords.length > 0) {
      const data = await getDrivingDirections(coords)
      if (data && data.routes && data.routes.length > 0) {
        setTotalTime(data.routes[0].duration)
        setDistance(data.routes[0].distance)
        displayRouteOnMap(data)
      }
    }
  }

  const googleOptimize = async (jobsToOptimize: RouteJob[]) => {
    return await googleMapsOptimizeRoute(state.startingLocation!, state.endingLocation!, jobsForCreateRoute, jobsToOptimize)
  }

  const displayOptimizedRoute = async () => {
    try {
      const sortedJobs = [...state.jobs].sort((a, b) => a.sortIndex - b.sortIndex)
      const rangeStartIndex = optimizeRange[0] - 1
      const rangeEndIndex = optimizeRange[1] - 1

      const stopsBefore = sortedJobs.slice(0, rangeStartIndex)
      const stopsInRange = sortedJobs.slice(rangeStartIndex, rangeEndIndex + 1)
      const stopsAfter = sortedJobs.slice(rangeEndIndex + 1)

      const jobsInRange = stopsInRange.filter(job => job.jobId !== null)

      if (jobsInRange.length === 0) {
        notification.warning({
          message: 'No jobs to optimize',
          description: 'The selected range contains no jobs to optimize.'
        })
        return
      }

      const googleData = await googleOptimize(jobsInRange)

      if (!googleData || !googleData.routes || !googleData.routes[0] || !googleData.routes[0].optimizedIntermediateWaypointIndex) {
        alert('Route optimization failed. Please try again or check your network connection.')
        return
      }

      const optimizedJobs = googleData.routes[0].optimizedIntermediateWaypointIndex.map((x: number) => {
        return jobsInRange[x]
      }).filter(Boolean)

      const tenantStopsInRange = stopsInRange.filter(job => job.tenantLocationId !== null)
      const startTenantInRange = tenantStopsInRange.find(job => job.sortIndex === rangeStartIndex)
      const endTenantInRange = tenantStopsInRange.find(job => job.sortIndex === rangeEndIndex)

      const optimizedRange: RouteJob[] = []
      if (startTenantInRange) {
        optimizedRange.push(startTenantInRange)
      }
      optimizedRange.push(...optimizedJobs)
      if (endTenantInRange && endTenantInRange.id !== startTenantInRange?.id) {
        optimizedRange.push(endTenantInRange)
      }

      const finalRouteJobs = [
        ...stopsBefore,
        ...optimizedRange,
        ...stopsAfter
      ].map((job, index) => ({
        ...job,
        sortIndex: index
      }))

      dispatch({ type: 'ROUTE_JOBS_LOADED', value: finalRouteJobs })
    } catch (error) {
      notification.error({
        message: 'Route optimization failed',
        description: 'Please try again or check your network connection.'
      })
    }
  }

  const removeCurrentRouteLayersFromMap = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap()
      const layersToRemove = ['route', 'routearrows']
      const sourcesToRemove = ['route']

      layersToRemove.forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId)
        }
      })
      
      sourcesToRemove.forEach(sourceId => {
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId)
        }
      })
    }
  }

  const displayRouteOnMap = (data: any, shouldFitBounds: boolean = true) => {
    const attemptAddRoute = () => {
      try {
        if (!mapRef.current) {
          return false
        }

        const map = mapRef.current.getMap()
        
        const trip = data.trips ? data.trips[0] : data.routes?.[0]
        if (!trip || !trip.geometry || !trip.geometry.coordinates) {
          return false
        }

        setCurrentRouteData(data)
        removeCurrentRouteLayersFromMap()

        const routeGeoJSON: Feature<LineString> = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: trip.geometry.coordinates
          }
        }

        map.addSource('route', {
          type: 'geojson',
          data: routeGeoJSON
        })

        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#FF5C35',
            'line-width': 4,
            'line-opacity': 0.8
          }
        })

        map.addLayer({
          id: 'routearrows',
          type: 'symbol',
          source: 'route',
          layout: {
            'symbol-placement': 'line',
            'text-field': '▶',
            'text-size': ['interpolate', ['linear'], ['zoom'], 12, 24, 22, 60],
            'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 30, 22, 160],
            'text-keep-upright': false
          },
          paint: {
            'text-color': '#FF5C35',
            'text-halo-color': 'hsl(55, 11%, 96%)',
            'text-halo-width': 3
          }
        })

        if (shouldFitBounds) {
          const bounds = new mapboxgl.LngLatBounds()
          trip.geometry.coordinates.forEach((coord: [number, number]) => {
            bounds.extend(coord)
          })
          map.fitBounds(bounds, { padding: 50 })
        }
        
        return true // Success
      } catch (error) {
        return false // Failed
      }
    }

    // Try immediately first
    if (attemptAddRoute()) {
      return
    }

    // If that failed, try with increasing delays
    const retryAttempts = [200, 500, 1000, 1500, 2000]
    let currentAttempt = 0

    const retryAddRoute = () => {
      if (currentAttempt >= retryAttempts.length) {
        return // Give up after max attempts
      }

      setTimeout(() => {
        if (attemptAddRoute()) {
          return // Success, stop retrying
        }
        
        currentAttempt++
        retryAddRoute() // Try again
      }, retryAttempts[currentAttempt])
    }

    retryAddRoute()
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id && state.jobs) {
      dispatch({ 
        type: 'ROUTE_JOBS_LOADED', 
        value: dragRouteOrderChanged(active.id, over.id, state) 
      })
    }
  }

  const handleStartingLocationChange = (value: string) => {
    const newStartingLocation = locations.find(l => l.id === value)
    const finalJobs = prepareRouteJobsForSave(state.jobs, routeId, newStartingLocation, state.endingLocation)
    dispatch({ type: 'ROUTE_STARTING_LOCATION_CHANGED', value: newStartingLocation })
    dispatch({ type: 'ROUTE_JOBS_LOADED', value: finalJobs })
  }

  const handleEndingLocationChange = (value: string) => {
    const newEndingLocation = locations.find(l => l.id === value)
    const finalJobs = prepareRouteJobsForSave(state.jobs, routeId, state.startingLocation, newEndingLocation)
    dispatch({ type: 'ROUTE_ENDING_LOCATION_CHANGED', value: newEndingLocation })
    dispatch({ type: 'ROUTE_JOBS_LOADED', value: finalJobs })
  }
  
  useEffect(() => {
    if (newRoute) {
      setIsTourOpen(false)
      setIsAddJobsDrawerOpen(true)
    }
  }, [newRoute])

  useEffect(() => {
    if (route) {
      dispatch({ type: 'ROUTE_LOADED', value: route })
    }
  }, [route])

  useEffect(() => {
    if (shopLocation && state.jobs) {
      const jobLocations = buildJobLocations(state.jobs, jobsForCreateRoute)

      const shopLocationObj: RouteLocation = {
        id: `shop-${shopLocation.id}`,
        jobName: 'My Shop',
        latitude: shopLocation.latitude,
        longitude: shopLocation.longitude,
        address1: shopLocation.address1,
        address2: shopLocation.address2,
        city: shopLocation.city,
        stateCode: shopLocation.stateCode,
        zip: shopLocation.zip
      }

      setLocations([shopLocationObj, ...jobLocations])

      if (jobLocations.length > 0) {    
        if (state.jobs.length === 0) {
          const shopRouteJob: RouteJob = {
            id: 0,
            routeId: routeId,
            jobId: null,
            tenantLocationId: shopLocation.id,
            estimatedDurationInMinutes: 0,
            workAreaIds: '',
            tenantId: 1,
            sortIndex: 0
          }
          
          dispatch({ type: 'ROUTE_JOBS_LOADED', value: [shopRouteJob] })
        }
      }
    }
  }, [shopLocation, jobsForCreateRoute, state.jobs])

  useEffect(() => {
    if (state.startingLocation 
        && state.startingLocation.longitude !== viewState.longitude
        && state.startingLocation.latitude !== viewState.latitude) {
      setViewState({
        longitude: state.startingLocation.longitude,
        latitude: state.startingLocation.latitude,
        zoom: DEFAULT_MAP_ZOOM,
        bearing: 0,
        pitch: 0,
        padding: { top: 0, bottom: 0, left: 0, right: 0 }
      })
    }
  }, [state.startingLocation])

  useEffect(() => {
    if (route && state.route.name && routeNameHeaderRef.current && !hasScrolled.current) {
      routeNameHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setTimeout(() => {
        window.scrollBy({ top: -24, left: 0, behavior: 'smooth' })
      }, 300)
      hasScrolled.current = true
    }
    if (!route) {
      hasScrolled.current = false
    }
  }, [route, state.route.name])

  useEffect(() => {
    if (routeJobs && (!state.jobs.length || routeJobs.length !== state.jobs.length) && !hasInitiallyLoadedRouteJobs) {
      dispatch({ type: 'ROUTE_JOBS_LOADED', value: routeJobs })
      setHasInitiallyLoadedRouteJobs(true)
    }
  }, [routeJobs, state.jobs.length])

  useEffect(() => {
    if (state.jobs.length > 0) {       
      const firstLocation = getStartingLocation(state.jobs, shopLocation, jobsForCreateRoute)
      if (firstLocation) {
        dispatch({ type: 'ROUTE_STARTING_LOCATION_CHANGED', value: firstLocation })
      }
    }
  }, [state.jobs, jobsForCreateRoute, shopLocation])

  useEffect(() => {
    const isCurrentEndingLocationShop = state.endingLocation?.id.startsWith('shop-')
    if (state.jobs.length > 0 && !isCurrentEndingLocationShop) {     
      const lastLocation = getEndingLocation(state.jobs, shopLocation, jobsForCreateRoute)
      if (lastLocation && (!state.endingLocation || state.endingLocation.id !== lastLocation.id)) {
        dispatch({ type: 'ROUTE_ENDING_LOCATION_CHANGED', value: lastLocation })
      }
    }
  }, [state.jobs, jobsForCreateRoute, shopLocation, state.endingLocation])


  useEffect(() => {
    if (isMapLoaded && state.startingLocation && state.endingLocation && state.jobs.length > 0) {
      displayDrivingDirections()
    }
  }, [isMapLoaded, state.startingLocation, state.endingLocation, state.jobs])

  // Pre-render AddJobsByMap when data is ready
  useEffect(() => {
    // Only pre-render if we have data and haven't already started pre-rendering
    if (jobsForCreateRoute && shopLocation && !shouldPreRenderAddJobsMap && !isAddJobsDrawerOpen) {
      // Delay pre-render to avoid blocking initial page load
      const timeout = setTimeout(() => {
        setShouldPreRenderAddJobsMap(true)
      }, 2000) // 2 second delay to ensure RouteView is fully loaded
      
      return () => clearTimeout(timeout)
    }
  }, [jobsForCreateRoute, shopLocation, shouldPreRenderAddJobsMap, isAddJobsDrawerOpen])

  // Cleanup pre-render when AddJobs drawer opens to save memory
  useEffect(() => {
    if (isAddJobsDrawerOpen && shouldPreRenderAddJobsMap) {
      setShouldPreRenderAddJobsMap(false)
    }
  }, [isAddJobsDrawerOpen, shouldPreRenderAddJobsMap])

  useEffect(() => {
    if (state.startingLocation && state.startingLocation.id.startsWith('job-') && state.jobs.length > 0) {
      const jobId = parseInt(state.startingLocation.id.replace('job-', ''))
      const jobStillExists = state.jobs.some(job => job.jobId === jobId)
      
      if (!jobStillExists) {
        dispatch({ type: 'ROUTE_STARTING_LOCATION_CHANGED', value: undefined })
      }
    }
    
    if (state.endingLocation && state.endingLocation.id.startsWith('job-') && state.jobs.length > 0) {
      const jobId = parseInt(state.endingLocation.id.replace('job-', ''))
      const jobStillExists = state.jobs.some(job => job.jobId === jobId)
      
      if (!jobStillExists) {
        dispatch({ type: 'ROUTE_ENDING_LOCATION_CHANGED', value: undefined })
      }
    }
  }, [state.jobs, state.startingLocation, state.endingLocation])

  useEffect(() => {
    const stopCount = state.jobs.length
    if (stopCount > 0) {
      setOptimizeRange([1, Math.min(stopCount, 23)])
    }
  }, [state.jobs.length])

  const tourSteps = [
    {
      title: 'Optimize Drive Time',
      description: 'Click this button to automatically reorder your jobs for the shortest total drive time. The system will analyze all locations and create the most efficient route.',
      target: () => document.getElementById('optimize-drive-time-button') as HTMLElement || null,
    },
    {
      title: 'Drive Time & Distance',
      description: 'View the total estimated drive time and distance for your route. This updates automatically as you add or rearrange jobs.',
      target: () => document.getElementById('drive-time-display') as HTMLElement || null,
    },
    {
      title: 'Starting Location',
      description: 'Select where your route begins. This is typically your shop location, but you can choose any location from your route as the starting point.',
      target: () => document.getElementById('starting-location-selector') as HTMLElement || null,
    },
    {
      title: 'Drag to Reorder Jobs',
      description: 'Click and drag this handle to reorder jobs in your route. The system will automatically recalculate drive times and distances.',
      target: () => document.getElementById('drag-handle') as HTMLElement || null,
    },
    {
      title: 'Edit Job Details',
      description: 'Click on any job name to open the job editor where you can modify job details, add notes, or remove the job from the route.',
      target: () => document.getElementById('job-name') as HTMLElement || null,
    },
    {
      title: 'Map View Toggle',
      description: 'Switch between street view and satellite view to get the best perspective for planning your route.',
      target: () => document.getElementById('map-style-dropdown') as HTMLElement || null,
    },
    {
      title: 'Route Settings',
      description: 'Click on the Settings tab to access advanced route configuration options, including crew assignments, work day settings, and route scheduling.',
      target: () => document.querySelector('#route-view-tabs [data-node-key="2"]') as HTMLElement || null,
    },
    {
      title: 'Route Status',
      description: 'This tag shows whether your route is currently active or inactive. Active routes appear in the calendar and are available for scheduling, while inactive routes are saved but not scheduled.',
      target: () => document.getElementById('route-status-tag') as HTMLElement || null,
    },
    {
      title: 'Manage Jobs',
      description: 'Click here to add new jobs to your route or remove existing ones. This opens the job management drawer where you can search and select jobs.',
      target: () => document.getElementById('manage-jobs-button') as HTMLElement || null,
    },
    {
      title: 'Save Route',
      description: 'Save your route changes and activate or deactivate the route. This button is enabled when you make changes to the route.',
      target: () => document.getElementById('save-route-button') as HTMLElement || null,
    },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Top Tabs and Route Name */}
      <div style={{ background: '#fff', padding: '0 24px 0 24px', borderBottom: '1px solid #eee' }}>
        <RouteViewTitleRow
          state={state}
          routeJobs={routeJobs ?? null}
          crew={crew}
          routeNameHeaderRef={routeNameHeaderRef}
          onBackToList={onBackToList}
          onAddJobsDrawerOpen={() => setIsAddJobsDrawerOpen(true)}
          onOpenSaveRouteModal={() => setIsSaveRouteModalOpen(true)}
          onOpenTour={() => setIsTourOpen(true)}
        />
        <Tabs
          id="route-view-tabs"
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ marginBottom: 0 }}
        />
      </div>
      {/* Main Content Panels */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {activeTab === '1' && (
          <div style={{ display: 'flex', flex: 1, minHeight: '64vh', maxHeight: '64vh', overflow: 'hidden' }}>
            {/* Left Panel */}
            <div style={{ width: 400, background: '#fafbfc', display: 'flex', flexDirection: 'column', overflow: 'scroll' }}>
              {/* Starting/Ending Location UI */}
              <div style={{ background: '#fff', padding: 20, marginBottom: 16 }}>
                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <OptimizeRouteSection
                    stopCount={state.jobs.length}
                    optimizeRange={optimizeRange}
                    onRangeChange={setOptimizeRange}
                    onOptimize={displayOptimizedRoute}
                    hasStartLocation={state.jobs.some(job => job.tenantLocationId !== null && job.sortIndex === 0)}
                    hasEndLocation={state.jobs.some(job => job.tenantLocationId !== null && job.sortIndex === state.jobs.length - 1)}
                  />
                </div>
              </div>

              <RouteViewJobList
                state={state}
                totalTime={totalTime}
                distance={distance}
                locations={locations}
                jobsForCreateRoute={jobsForCreateRoute}
                tenantLocations={tenantLocations}
                hoveredJobSortIndex={hoveredJobSortIndex}
                setHoveredJobSortIndex={setHoveredJobSortIndex}
                onStartingLocationChange={handleStartingLocationChange}
                onEndingLocationChange={handleEndingLocationChange}
                onDragEnd={handleDragEnd}
                onJobClick={(jobId) => {
                  setEditJobId(jobId)
                  setIsAddJobsDrawerOpen(true)
                }}
              />
            </div>
            <RouteViewMap
              state={state}
              mapRef={mapRef}
              hoveredJobSortIndex={hoveredJobSortIndex}
              setHoveredJobSortIndex={setHoveredJobSortIndex}
              setIsMapLoaded={setIsMapLoaded}
              mapStyle={mapStyle}
              setMapStyle={setMapStyle}
              isStyleChanging={isStyleChanging}
              setIsStyleChanging={setIsStyleChanging}
              currentRouteData={currentRouteData}
              displayRouteOnMap={displayRouteOnMap}
              displayDrivingDirections={displayDrivingDirections}
              shopLocation={shopLocation}
              tenantLocations={tenantLocations}
              onManageShopAddress={() => setIsManageShopAddressModalOpen(true)}
              jobsForCreateRoute={jobsForCreateRoute}
            />
          </div>
        )}
        {activeTab === '2' && (
          <RouteViewSettings 
            route={route}
            state={state}
            crew={crew}
            setIsUpdateRouteModalOpen={setIsUpdateRouteModalOpen}
          />
        )}
      </div>
      <AddJobs
        isOpen={isAddJobsDrawerOpen}
        initialMapViewState={viewState}
        onClose={() => {
          setIsAddJobsDrawerOpen(false)
          setEditJobId(null)
        }}
        onSaveSuccess={(jobs: RouteJob[]) => {
          setIsAddJobsDrawerOpen(false)
          dispatch({ type: 'ROUTE_JOBS_LOADED', value: jobs })
          setEditJobId(null)
          const viewed = window.localStorage.getItem('route-view-tour-viewed') === 'true'
          setIsTourOpen(!viewed && jobs.length > 0)
        }}
        existingJobs={state.jobs}
        routeId={routeId}
        editJobId={editJobId}
        shopLocation={shopLocation || null}
      />
      <ManageShopAddressModal
        isOpen={isManageShopAddressModalOpen} 
        onClose={() => setIsManageShopAddressModalOpen(false)}
        proximity={`${viewState.longitude},${viewState.latitude}`}
        shopAddress={tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)}
        onAddressUpdate={(coordinates) => {
          setViewState({
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            zoom: DEFAULT_MAP_ZOOM,
            bearing: 0,
            pitch: 0,
            padding: { top: 0, bottom: 0, left: 0, right: 0 }
          })
          
          const updatedStartingLocation: RouteLocation = {
            id: `shop-${shopLocation?.id || 'temp'}`,
            jobName: 'My Shop',
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
            address1: shopLocation?.address1 || '',
            address2: shopLocation?.address2 || '',
            city: shopLocation?.city || '',
            stateCode: shopLocation?.stateCode || '',
            zip: shopLocation?.zip || ''
          }
          
          dispatch({ type: 'ROUTE_STARTING_LOCATION_CHANGED', value: updatedStartingLocation })
          
          if (state.endingLocation && state.endingLocation.id.startsWith('shop-')) {
            dispatch({ type: 'ROUTE_ENDING_LOCATION_CHANGED', value: updatedStartingLocation })
          }
          
          setLocations([updatedStartingLocation, ...buildJobLocations(routeJobs, jobsForCreateRoute)])
          
          if (isMapLoaded) {
            displayDrivingDirections()
          }
        }}
      />
      <UpdateRouteModal
        isOpen={isUpdateRouteModalOpen}
        onClose={() => setIsUpdateRouteModalOpen(false)}
        crew={crew}
        route={state.route}
        onSave={() => {
          setIsUpdateRouteModalOpen(false)
        }}
        onShowRegenerateModal={(pendingRoute) => {
          setPendingRouteUpdate(pendingRoute)
          setIsRegenerateRoutesModalOpen(true)
        }}
      />
      
      <RegenerateRoutesModal
        visible={isRegenerateRoutesModalOpen}
        onCancel={() => {
          setIsRegenerateRoutesModalOpen(false)
          setPendingRouteUpdate(null)
        }}
        onSave={() => {
          if (pendingRouteUpdate) {
            updateRouteMutation.mutate(pendingRouteUpdate, {
              onSuccess: () => {
                regenerateRoute.mutate(pendingRouteUpdate.id)
                setIsRegenerateRoutesModalOpen(false)
                setPendingRouteUpdate(null)
              }
            })
          }
        }}
      />
      
      <SaveRouteModal
        open={isSaveRouteModalOpen}
        onCancel={() => setIsSaveRouteModalOpen(false)}
        route={state.route}
        originalActiveState={state.route.active}
        initialActiveState={state.route.active}
        onSave={(saveStrategy) => {
          const jobsToSave = prepareRouteJobsForSave(state.jobs, routeId, state.startingLocation, state.endingLocation)
          
          if (saveStrategy === 'regenerate') {
            const routeToUpdate = {
              ...state.route,
            }
            updateRouteMutation.mutate(routeToUpdate, {
              onSuccess: () => {
                insertUpdateRouteJobsMutation.mutate({ routeId: routeId, request: jobsToSave }, {
                  onSuccess: () => {
                    refetchRouteJobs()
                    regenerateRoute.mutate(routeToUpdate.id)
                  }
                })
              }
            })
          } else if (saveStrategy === 'update') {
            const routeToUpdate = {
              ...state.route,
            }
            updateRouteMutation.mutate(routeToUpdate, {
              onSuccess: () => {
                insertUpdateRouteJobsMutation.mutate({ routeId: routeId, request: jobsToSave }, {
                  onSuccess: () => {
                    refetchRouteJobs()
                  }
                })
              }
            })
          } else if (saveStrategy === 'update-then-activate') {
            const routeToUpdate = {
              ...state.route,
            }
            updateRouteMutation.mutate(routeToUpdate, {
              onSuccess: () => {
                insertUpdateRouteJobsMutation.mutate({ routeId: routeId, request: jobsToSave }, {
                  onSuccess: () => {
                    refetchRouteJobs()
                    saveRouteActiveMutation.mutate({ routeId: routeId, active: true })
                    window.posthog.capture('route-activated', { routeId: routeId })
                  }
                })
              }
            })
          } else if (saveStrategy === 'update-then-deactivate') {
            const routeToUpdate = {
              ...state.route,
            }
            updateRouteMutation.mutate(routeToUpdate, {
              onSuccess: () => {
                insertUpdateRouteJobsMutation.mutate({ routeId: routeId, request: jobsToSave }, {
                  onSuccess: () => {
                    refetchRouteJobs()
                    saveRouteActiveMutation.mutate({ routeId: routeId, active: false })
                  }
                })
              }
            })
          }
        }}
      />

      {/* Hidden pre-render of AddJobsByMap for performance */}
      {shouldPreRenderAddJobsMap && jobsForCreateRoute && (
        <div style={{ 
          position: 'absolute', 
          top: -9999, 
          left: -9999, 
          width: '800px', 
          height: '600px', 
          visibility: 'hidden',
          pointerEvents: 'none' // Prevent any interactions
        }}>
          <AddJobsByMapPreRender
            jobs={jobsForCreateRoute}
            initialMapViewState={viewState}
          />
        </div>
      )}
      
      <Tour
        open={isTourOpen}
        closable={true}
        onClose={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('route-view-tour-viewed', 'true')
        }}
        onFinish={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('route-view-tour-viewed', 'true')
        }}
        steps={tourSteps.filter(step => step.target() !== null)}
        type="default"
        disabledInteraction={true}
      />
    </div>
  )
}

export default RouteView